/**
 * Endless mode: 5 random effehat per round. After each round, cumulative
 * score + share grid + "play another round" CTA. Sessions persist across
 * rounds in localStorage so we can show running totals + best round.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { EffehGame, attemptSquare, type EffehInput, type EffehResult } from '../components/EffehGame'

type RoundData = {
  round_id: string
  effehat: EffehInput[]
  max_attempts_per_effeh: number
  max_score: number
  reason?: string
}

type SessionStats = {
  rounds_played: number
  best_round_score: number
  total_score: number
  total_effehat: number
  total_perfect_effehat: number
}

const SESSION_KEY = 'effeh:session_stats'
const ROUND_SIZE = 5
const ROUND_MAX = ROUND_SIZE * 100

function loadSessionStats(): SessionStats {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (raw) return JSON.parse(raw) as SessionStats
  } catch { /* ignore */ }
  return { rounds_played: 0, best_round_score: 0, total_score: 0, total_effehat: 0, total_perfect_effehat: 0 }
}

function saveSessionStats(s: SessionStats) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)) } catch { /* ignore */ }
}

export default function Play() {
  const [round, setRound] = useState<RoundData | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [currentIdx, setCurrentIdx] = useState(0)            // 0..ROUND_SIZE-1
  const [results, setResults] = useState<EffehResult[]>([])  // per-effeh results in this round
  const [showSummary, setShowSummary] = useState(false)      // user-driven transition to RoundSummary
  const [session, setSession] = useState<SessionStats>(() => loadSessionStats())

  // Fetch a fresh round on mount + whenever user starts a new one
  function fetchRound() {
    setRound(null)
    setCurrentIdx(0)
    setResults([])
    setShowSummary(false)
    setLoadErr(null)
    fetch('/api/round')
      .then((r) => r.json() as Promise<RoundData>)
      .then((d) => setRound(d))
      .catch((e: unknown) => setLoadErr(String(e)))
  }

  useEffect(() => { fetchRound() }, [])

  function onEffehDone(result: EffehResult) {
    const newResults = [...results, result]
    setResults(newResults)
    // Persist session stats as soon as the last effeh resolves — that way even
    // if the user closes the tab before clicking "النتيجة", the round counts.
    if (newResults.length >= (round?.effehat.length ?? ROUND_SIZE)) {
      const roundMovie = newResults.reduce((s, r) => s + r.movieScore, 0)
      const roundBonus = newResults.reduce((s, r) => s + r.bonusScore, 0)
      const roundTotal = roundMovie + roundBonus
      // "Perfect effeh" = full 150 (movie + actor bonus, both on attempt 1).
      const perfect = newResults.filter((r) => r.totalScore === 150).length
      const updated: SessionStats = {
        rounds_played: session.rounds_played + 1,
        best_round_score: Math.max(session.best_round_score, roundTotal),
        total_score: session.total_score + roundTotal,
        total_effehat: session.total_effehat + newResults.length,
        total_perfect_effehat: session.total_perfect_effehat + perfect,
      }
      setSession(updated)
      saveSessionStats(updated)
    }
  }

  /** User-driven advance: go to next effeh, or reveal the round summary if this was the last. */
  function advance() {
    const nextIdx = currentIdx + 1
    if (nextIdx >= (round?.effehat.length ?? ROUND_SIZE)) {
      setShowSummary(true)
    } else {
      setCurrentIdx(nextIdx)
    }
  }

  if (loadErr) return <Shell stats={session}><p className="text-rose-400 text-center mt-12">حصل خطأ: {loadErr}</p></Shell>
  if (!round) return <Shell stats={session}><Skeleton /></Shell>
  if (round.effehat.length === 0) {
    return (
      <Shell stats={session}>
        <p className="text-ink-300 text-center mt-12">لسه مفيش إفيهات في القاعدة.</p>
        {round.reason && <p className="text-xs text-ink-500 mt-2 text-center">({round.reason})</p>}
      </Shell>
    )
  }

  const currentEffeh = round.effehat[currentIdx]
  const currentEffehDone = results.length > currentIdx
  const isLastEffeh = currentIdx >= round.effehat.length - 1

  return (
    <Shell stats={session}>
      {/* Round progress bar */}
      <div className="mb-6 effeh-fade-up">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-500">
            {showSummary ? 'الجولة خلصت' : `إفيه ${numAr(currentIdx + 1)} من ${numAr(round.effehat.length)}`}
          </div>
          <div className="text-xs text-ink-500 tabular-nums">
            {results.reduce((s, r) => s + r.movieScore, 0)} / {round.max_score}
            {(() => {
              const bonus = results.reduce((s, r) => s + r.bonusScore, 0)
              return bonus > 0 ? <span className="text-amber-300"> ⭐ +{bonus}</span> : null
            })()}
          </div>
        </div>
        <div className="flex gap-1.5">
          {round.effehat.map((_, i) => {
            const r = results[i]
            let cls = 'bg-ink-700'
            if (r != null) {
              // Color by movie score (bonus is additive, not gating)
              cls = r.movieScore === 100 ? 'bg-leaf-600'
                : r.movieScore >= 60 ? 'bg-amber-500'
                : r.movieScore > 0 ? 'bg-amber-700'
                : 'bg-rose-600'
            } else if (i === currentIdx && !showSummary) cls = 'bg-amber-500'
            return <div key={i} className={`flex-1 h-1.5 rounded-full ${cls}`} />
          })}
        </div>
      </div>

      {/* Current effeh (+ next button once done) OR round summary */}
      {!showSummary && currentEffeh ? (
        <>
          <EffehGame
            key={currentEffeh.id}
            effeh={currentEffeh}
            puzzleKey={round.round_id}
            maxAttempts={round.max_attempts_per_effeh}
            onDone={onEffehDone}
          />
          {currentEffehDone && (
            <div className="mt-6 effeh-fade-up">
              <button
                onClick={advance}
                className="w-full py-3.5 rounded-md bg-amber-500 text-ink-900 font-bold text-base tracking-wide hover:bg-amber-300 transition"
              >
                {isLastEffeh ? 'النتيجة ←' : 'الإفيه الجاي ←'}
              </button>
            </div>
          )}
        </>
      ) : (
        <RoundSummary
          results={results}
          effehat={round.effehat}
          maxScore={round.max_score}
          session={session}
          onPlayAgain={fetchRound}
        />
      )}
    </Shell>
  )
}

function RoundSummary({
  results, effehat, maxScore, session, onPlayAgain,
}: {
  results: EffehResult[]
  effehat: EffehInput[]
  maxScore: number
  session: SessionStats
  onPlayAgain: () => void
}) {
  const totalMovie = results.reduce((s, r) => s + r.movieScore, 0)
  const totalBonus = results.reduce((s, r) => s + r.bonusScore, 0)
  const [shareToast, setShareToast] = useState(false)

  const shareText = useMemo(() => {
    const movieRow = results.map((r) => attemptSquare(r.movieAttempt)).join('')
    const actorRow = results.map((r) => attemptSquare(r.actorAttempt)).join('')
    const bonusSuffix = totalBonus > 0 ? `  ⭐ +${totalBonus}` : ''
    return `إفيه — جولة\n🎬 ${movieRow}\n🎭 ${actorRow}\n${totalMovie}/${maxScore}${bonusSuffix}\nhttps://2efeh.com`
  }, [results, totalMovie, totalBonus, maxScore])

  async function share() {
    try {
      await navigator.clipboard.writeText(shareText)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 1800)
    } catch { /* clipboard blocked */ }
  }

  // Verdict is based on the headline movie score (out of /maxScore).
  const pct = Math.round((totalMovie / maxScore) * 100)
  const verdict =
    pct === 100 ? 'سينيفيل أصلي 🎬'
    : pct >= 80 ? 'إنت متابع ميه ميه'
    : pct >= 60 ? 'حلو جداً'
    : pct >= 40 ? 'فيه جزء كويس'
    : pct >= 20 ? 'هنحسنها الجولة الجاية'
    : 'بكرة بنبدأ تاني — العمر طويل'

  return (
    <div className="effeh-fade-up">
      <div className="text-center mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-amber-500 mb-3">خلصت الجولة</div>
        <div className="mx-auto w-16 effeh-rule opacity-60 mb-4" />
        <div className="text-7xl font-display text-ink-50 tabular-nums">
          {totalMovie}<span className="text-3xl text-ink-500"> / {maxScore}</span>
        </div>
        {totalBonus > 0 && (
          <div className="text-amber-300 mt-3 tabular-nums">
            ⭐ +{totalBonus} نقطة إضافية
          </div>
        )}
        <div className="text-sm text-ink-300 mt-4">{verdict}</div>
      </div>

      {/* Per-effeh recap */}
      <div className="rounded-xl bg-ink-700/20 border border-ink-700/60 p-4 mb-5 space-y-2">
        {results.map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-amber-500 tabular-nums w-6 text-center">{numAr(i + 1)}</span>
              <span className="text-ink-50 truncate effeh-quote">{effehat[i]?.line_ar ?? '?'}</span>
            </div>
            <div className="flex items-center gap-1 text-base shrink-0">
              <span>{attemptSquare(r.movieAttempt)}</span>
              <span>{attemptSquare(r.actorAttempt)}</span>
              <span className="text-ink-300 tabular-nums w-12 text-end ms-2">
                {r.movieScore}{r.bonusScore > 0 && <span className="text-amber-300">+{r.bonusScore}</span>}
              </span>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onPlayAgain}
        className="w-full py-3.5 rounded-md bg-amber-500 text-ink-900 font-bold text-base tracking-wide hover:bg-amber-300 transition mb-2"
      >
        العب جولة تانية
      </button>
      <button
        onClick={share}
        className="w-full py-3 rounded-md border border-ink-700 text-ink-100 hover:bg-ink-700/40 transition"
      >
        شارك النتيجة 📋
      </button>
      {shareToast && <p className="text-center text-leaf-400 text-sm mt-2">اتنسخت!</p>}
      <pre className="mt-4 text-center text-sm text-ink-300 whitespace-pre-wrap font-body">{shareText}</pre>

      {/* Session stats */}
      <div className="mt-10 pt-6 border-t border-ink-700/60">
        <div className="mx-auto w-12 effeh-rule opacity-60 mb-3" />
        <div className="text-xs uppercase tracking-[0.3em] text-ink-500 mb-3 text-center">إحصائياتك</div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <SessionStatCell label="جولات" value={session.rounds_played} />
          <SessionStatCell label="أعلى جولة" value={session.best_round_score} />
          <SessionStatCell label="إجمالي" value={session.total_score} />
        </div>
      </div>
    </div>
  )
}

function SessionStatCell({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-ink-700/20 border border-ink-700/60 px-3 py-3">
      <div className="text-2xl font-display text-ink-50 tabular-nums">{value}</div>
      <div className="text-xs text-ink-500 mt-1">{label}</div>
    </div>
  )
}

function Shell({ children, stats }: { children: React.ReactNode; stats: SessionStats }) {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-ink-700/50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl">🎬</span>
            <span className="font-display text-xl text-amber-300">إفيه</span>
          </Link>
          <div className="flex items-center gap-3 text-xs text-ink-500">
            {stats.rounds_played > 0 && (
              <span className="tabular-nums">
                {numAr(stats.rounds_played)} جولة · {stats.total_score} نقطة
              </span>
            )}
            <Link to="/daily" className="text-amber-300 hover:underline">يومي</Link>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-8">{children}</main>
      <footer className="text-center text-xs text-ink-500 py-6">
        نسخة تجريبية · made with 💛 in Cairo
      </footer>
    </div>
  )
}

function Skeleton() {
  return (
    <div className="animate-pulse">
      <div className="flex gap-1.5 mb-6">
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="flex-1 h-1.5 bg-ink-700 rounded-full" />)}
      </div>
      <div className="rounded-2xl border border-ink-700/60 bg-ink-700/20 p-12 mb-6">
        <div className="h-8 bg-ink-700 rounded mb-3" />
        <div className="h-8 bg-ink-700 rounded w-3/4 mx-auto" />
      </div>
      <div className="space-y-3">
        <div className="h-12 bg-ink-700/50 rounded-xl" />
        <div className="h-12 bg-ink-700/50 rounded-xl" />
      </div>
    </div>
  )
}

function numAr(n: number): string {
  const digits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
  return String(n).split('').map((d) => digits[Number(d)] ?? d).join('')
}
